import { t } from '../i18n';
import { isBrowserOffline } from '../stores/network-status.svelte.js';
import { formatNumber } from './number-format.js';
import { formatDateTime } from './date-format.js';
import { ApiNetworkError, ApiServiceError } from '../types/api.js';

export function networkErrorMessage(): string {
	return isBrowserOffline() ? t('checkInternetConnection') : t('serverUnreachable');
}

export function getErrorMessage(e: unknown): string {
	if (e instanceof ApiServiceError) {
		return getErrorCodeMessage(e.errorCode, e.errorData);
	}
	if (e instanceof ApiNetworkError) {
		return networkErrorMessage();
	}
	return t('unexpectedError');
}

export function getErrorCodeMessage(errorCode: string, errorData: unknown): string {
	if (errorCode === 'AuthorizationError') {
		return authorizationErrorMessage(errorData);
	}
	if (errorCode === 'ProductLackOfAvailableStock') {
		return stockAvailabilityErrorMessage(errorData);
	}
	if (errorCode === 'InsufficientCashHolding') {
		return insufficientCashHoldingErrorMessage(errorData);
	}
	return t(errorCode, errorDataInterpolation(errorData));
}

export function getBackgroundJobErrorMessage(errorCode: string | null, errorData: unknown): string {
	if (errorCode === null || t(errorCode) === errorCode) {
		return t('genericError');
	}
	return getErrorCodeMessage(errorCode, errorData);
}

function authorizationErrorMessage(errorData: unknown): string {
	const missing = extractMissingPermissions(errorData);
	if (missing.length === 0) {
		return t('AuthorizationError');
	}
	const labels = missing.map((p) => t(`permission-${p}`).toUpperCase());
	return `${t('detailedAuthorizationError')} ${labels.join(', ')}`;
}

function extractMissingPermissions(errorData: unknown): string[] {
	if (errorData == null || typeof errorData !== 'object') {
		return [];
	}
	const value = (errorData as { missingPermissions?: unknown }).missingPermissions;
	if (!Array.isArray(value)) {
		return [];
	}
	return value.filter((x): x is string => typeof x === 'string');
}

export function errorDataInterpolation(errorData: unknown): Record<string, string> {
	if (errorData == null || typeof errorData !== 'object') {
		return {};
	}

	const interpolation: Record<string, string> = {};

	for (const [name, value] of Object.entries(errorData)) {
		const text = interpolatableText(value);
		if (text !== null) {
			interpolation[name] = text;
		}
	}

	return interpolation;
}

function interpolatableText(value: unknown): string | null {
	if (typeof value === 'string') {
		return value;
	}
	if (typeof value === 'number' || typeof value === 'boolean') {
		return String(value);
	}
	if (Array.isArray(value)) {
		const texts = value.map(interpolatableText);
		return texts.every((x) => x !== null) ? texts.join(', ') : null;
	}
	return null;
}

function stockAvailabilityErrorMessage(errorData: unknown): string {
	const detail = stockAvailabilityFailureDetail(errorData);
	if (detail == null) {
		return t('productLackOfAvailableStock');
	}
	return `${t('productLackOfAvailableStock')}\n${detail}`;
}

const BULLET_TEXT_INDENT = '\u00a0\u00a0\u00a0';

const STOCK_MOVEMENT_RULES = ['PhysicalStock', 'StockOutflow', 'Reservation'] as const;

type StockMovementRule = (typeof STOCK_MOVEMENT_RULES)[number];

type StockAvailabilityFailurePayload = {
	rule: string;
	productName: string;
	warehouseName: string;
	requested: number;
	available: number;
};

type StockAvailabilityFailure = Omit<StockAvailabilityFailurePayload, 'rule'> & {
	rule: StockMovementRule | null;
};

export function stockAvailabilityFailureDetail(errorData: unknown): string | null {
	const failures = extractStockAvailabilityFailures(errorData);
	if (failures.length === 0) {
		return null;
	}
	return failures.map(describeStockAvailabilityFailure).join('\n');
}

function extractStockAvailabilityFailures(errorData: unknown): StockAvailabilityFailure[] {
	if (errorData == null || typeof errorData !== 'object') return [];
	const value = (errorData as { failures?: unknown }).failures;
	if (!Array.isArray(value)) return [];
	return value.filter(isStockAvailabilityFailurePayload).map((payload) => ({
		...payload,
		rule: toStockMovementRule(payload.rule)
	}));
}

function toStockMovementRule(rule: string): StockMovementRule | null {
	return STOCK_MOVEMENT_RULES.find((knownRule) => knownRule === rule) ?? null;
}

function isStockAvailabilityFailurePayload(value: unknown): value is StockAvailabilityFailurePayload {
	if (value == null || typeof value !== 'object') return false;
	const candidate = value as Partial<StockAvailabilityFailurePayload>;
	return (
		typeof candidate.rule === 'string' &&
		typeof candidate.productName === 'string' &&
		typeof candidate.warehouseName === 'string' &&
		typeof candidate.requested === 'number' &&
		typeof candidate.available === 'number'
	);
}

function describeStockAvailabilityFailure(failure: StockAvailabilityFailure): string {
	const detail = t('specificProductLackOfAvailableStock', {
		productName: failure.productName,
		warehouseName: failure.warehouseName,
		requested: formatNumber(failure.requested),
		available: formatNumber(failure.available)
	});

	if (failure.rule !== 'PhysicalStock') return `• ${detail}`;

	return `• ${detail}\n${BULLET_TEXT_INDENT}${t('physicalStockShortageRemedy')}`;
}

function insufficientCashHoldingErrorMessage(errorData: unknown): string {
	const detail = insufficientCashHoldingDetail(errorData);
	if (detail == null) {
		return t('insufficientCashHolding');
	}
	return `${t('insufficientCashHolding')}\n${detail}`;
}

type InsufficientCashHoldingPayload = {
	moneyStorageName: string;
	currencyName: string;
	periodClosureDatetime?: string | null;
};

export function insufficientCashHoldingDetail(errorData: unknown): string | null {
	if (!isInsufficientCashHoldingPayload(errorData)) return null;
	if (errorData.periodClosureDatetime == null) {
		return t('insufficientCashHoldingInOpenPeriod', {
			currencyName: errorData.currencyName,
			moneyStorageName: errorData.moneyStorageName
		});
	}
	return t('insufficientCashHoldingInClosedPeriod', {
		currencyName: errorData.currencyName,
		moneyStorageName: errorData.moneyStorageName,
		periodClosureDatetime: formatDateTime(errorData.periodClosureDatetime)
	});
}

function isInsufficientCashHoldingPayload(value: unknown): value is InsufficientCashHoldingPayload {
	if (value == null || typeof value !== 'object') return false;
	const candidate = value as Partial<InsufficientCashHoldingPayload>;
	return (
		typeof candidate.moneyStorageName === 'string' &&
		typeof candidate.currencyName === 'string' &&
		(candidate.periodClosureDatetime == null ||
			typeof candidate.periodClosureDatetime === 'string')
	);
}
